/**
 * Login — split layout: credentials pane (left) + brand panel (right).
 * Drives `useAuth().signIn`; routes to `/orgs` on success. Lockout state
 * (5 failed attempts → 30 min) comes from auth-context and renders as a
 * live mm:ss countdown. `?expired` in the URL surfaces a session-expired
 * banner (the shell redirects here with that param on idle timeout).
 */
import { useCallback, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Clock3, ShieldAlert } from 'lucide-react'
import { bffUrl, useAuth } from '@/app/auth-context'
import { Button, Input } from '@/components/ui'
import { BrandPanel } from './brand-panel'
import { PasswordInput } from './password-input'
import { SsoButton } from './sso-button'
import { formatMmSs, useCountdown } from './use-countdown'

const MAX_ATTEMPTS = 5 // mirrors auth-context MAX_FAILED_ATTEMPTS

export const LoginPage = function LoginPage() {
  return (
    <div className="grid lg:grid-cols-[2fr_1fr]" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <LoginPane />
      <BrandPanel />
    </div>
  )
}

/* ───────────────────────── credentials pane ───────────────────────── */

function LoginPane() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as Record<string, unknown>
  const sessionExpired = search.expired !== undefined && search.expired !== false
  // OIDC callback failures redirect here with ?error=<reason> (routes/auth.ts)
  // rather than dumping a raw JSON page.
  const authError = typeof search.error === 'string' ? search.error : undefined
  // Self-service sign-up + the demo hint only exist in mock mode; real mode is
  // invite-only (Keycloak owns identity).
  const mockMode = (import.meta.env.VITE_AUTH_MODE ?? 'mock') === 'mock'
  const form = useLoginForm()

  const dismissBanner = useCallback(() => {
    void navigate({ to: '/login', search: {}, replace: true })
  }, [navigate])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--s-10) var(--s-12)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 384 }}>
        <PaneHeader />

        {sessionExpired && <ExpiredBanner onDismiss={dismissBanner} />}
        {authError && <AuthErrorBanner reason={authError} onDismiss={dismissBanner} />}

        <div style={{ marginTop: 'var(--s-6)' }}>
          <SsoButton disabled={form.submitting} />
        </div>

        <OrDivider />

        <LoginForm {...form} />

        <div
          style={{
            marginTop: 'var(--s-4)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--s-3)',
            fontSize: 'var(--fs-13)',
          }}
        >
          <Link
            to="/forgot-password"
            style={{ color: 'var(--link)', fontWeight: 'var(--fw-medium)' }}
          >
            Forgot password?
          </Link>
          {mockMode ? (
            <span style={{ color: 'var(--text-mute)' }}>
              No account?{' '}
              <Link
                to="/signup"
                style={{ color: 'var(--link)', fontWeight: 'var(--fw-medium)' }}
              >
                Create one
              </Link>
            </span>
          ) : (
            <span style={{ color: 'var(--text-mute)' }}>
              New here?{' '}
              <a
                href={`${bffUrl()}/api/v1/auth/register`}
                style={{ color: 'var(--link)', fontWeight: 'var(--fw-medium)' }}
              >
                Create an account
              </a>
            </span>
          )}
        </div>

        {mockMode && <DemoTip />}
      </div>
    </div>
  )
}

function PaneHeader() {
  return (
    <header>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
        <img src="/assets/logos/logo_mark.png" alt="" width={22} height={22} aria-hidden="true" />
        <span className="kicker">IntegraTrace · Console</span>
      </div>
      <h1
        style={{
          margin: 'var(--s-3) 0 var(--s-1)',
          fontFamily: 'var(--ff-display)',
          fontSize: 'var(--fs-28)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--tracking-tight)',
          color: 'var(--text)',
        }}
      >
        Sign in
      </h1>
      <p style={{ fontSize: 'var(--fs-13)', color: 'var(--text-mute)' }}>
        Continuous IAM observability across your clouds.
      </p>
    </header>
  )
}

/* ───────────────────────── form state ───────────────────────── */

type LoginFormState = {
  email: string
  password: string
  emailError?: string
  passwordError?: string
  formError: string | null
  submitting: boolean
  locked: boolean
  lockedUntil: number | null
  lockoutRemainingMs: number
  attemptsLeft: number
  onEmailChange: (v: string) => void
  onPasswordChange: (v: string) => void
  onEmailBlur: () => void
  onPasswordBlur: () => void
  onSubmit: (e: FormEvent<HTMLFormElement>) => void
}

function validateEmail(v: string): string | undefined {
  if (!v.trim()) return 'Email is required'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) {
    return 'Enter a valid email address (e.g. you@company.com)'
  }
  return undefined
}

function validatePassword(v: string): string | undefined {
  return v ? undefined : 'Password is required'
}

function useLoginForm(): LoginFormState {
  const { signIn, failedAttempts, lockedUntil } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [touched, setTouched] = useState({ email: false, password: false })
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Ticks every second while locked; re-renders the form so inputs re-enable
  // the moment the lockout window elapses.
  const lockoutRemainingMs = useCountdown(lockedUntil)
  const locked = lockoutRemainingMs > 0
  const emailError = touched.email ? validateEmail(email) : undefined
  const passwordError = touched.password ? validatePassword(password) : undefined

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (locked || submitting) return
      setTouched({ email: true, password: true })
      if (validateEmail(email) || validatePassword(password)) return
      setFormError(null)
      setSubmitting(true)
      signIn(email.trim(), password)
        .then(() => navigate({ to: '/orgs' }))
        .catch((err: unknown) => {
          setFormError(err instanceof Error ? err.message : 'Sign-in failed')
        })
        .finally(() => setSubmitting(false))
    },
    [locked, submitting, email, password, signIn, navigate],
  )

  return {
    email, password, emailError, passwordError, formError, submitting,
    locked, lockedUntil, lockoutRemainingMs,
    attemptsLeft: Math.max(0, MAX_ATTEMPTS - failedAttempts),
    onEmailChange: setEmail,
    onPasswordChange: setPassword,
    onEmailBlur: () => setTouched((t) => ({ ...t, email: true })),
    onPasswordBlur: () => setTouched((t) => ({ ...t, password: true })),
    onSubmit,
  }
}

/* ───────────────────────── form view ───────────────────────── */

function LoginForm(f: LoginFormState) {
  return (
    <form
      onSubmit={f.onSubmit}
      noValidate
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}
    >
      <Input
        label="Email"
        type="email"
        autoComplete="email"
        icon="user"
        placeholder="you@company.com"
        value={f.email}
        error={f.emailError}
        onChange={(e) => f.onEmailChange(e.currentTarget.value)}
        onBlur={f.onEmailBlur}
        disabled={f.locked || f.submitting}
      />
      <PasswordInput
        autoComplete="current-password"
        value={f.password}
        error={f.passwordError}
        onChange={(e) => f.onPasswordChange(e.currentTarget.value)}
        onBlur={f.onPasswordBlur}
        disabled={f.locked || f.submitting}
      />

      {f.locked && <LockoutAlert lockedUntil={f.lockedUntil} remainingMs={f.lockoutRemainingMs} />}
      {!f.locked && f.formError && (
        <ErrorAlert message={f.formError} attemptsLeft={f.attemptsLeft} />
      )}

      <Button type="submit" size="lg" loading={f.submitting} disabled={f.locked}>
        {f.submitting ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  )
}

/* ───────────────────────── banners & chrome ───────────────────────── */

function ExpiredBanner({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      role="status"
      style={{
        marginTop: 'var(--s-4)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--s-2)',
        padding: 'var(--s-3)',
        borderRadius: 'var(--r-sm)',
        background: 'var(--info-100)',
        border: '1px solid var(--info-200)',
        color: 'var(--info-700)',
        fontSize: 'var(--fs-13)',
        lineHeight: 'var(--lh-normal)',
      }}
    >
      <Clock3 size={15} strokeWidth={1.75} aria-hidden="true" style={{ flex: 'none', marginTop: 1 }} />
      <span style={{ flex: 1 }}>Your session expired. Sign in again to continue.</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss session-expired notice"
        style={{
          border: 0,
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          font: 'inherit',
          fontWeight: 'var(--fw-medium)',
          padding: 0,
        }}
      >
        Dismiss
      </button>
    </div>
  )
}

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Sign-in was cancelled. Try again to continue.',
  missing_code: 'Your sign-in session expired. Please sign in again.',
  expired_state: 'Your sign-in session expired. Please sign in again.',
  exchange_failed: "We couldn't complete sign-in. Please try again.",
}

function AuthErrorBanner({ reason, onDismiss }: { reason: string; onDismiss: () => void }) {
  const message = AUTH_ERROR_MESSAGES[reason] ?? 'Sign-in failed. Please try again.'
  return (
    <div
      role="alert"
      style={{
        marginTop: 'var(--s-4)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--s-2)',
        padding: 'var(--s-3)',
        borderRadius: 'var(--r-sm)',
        background: 'var(--err-100)',
        border: '1px solid var(--err-200)',
        color: 'var(--err-700)',
        fontSize: 'var(--fs-13)',
        lineHeight: 'var(--lh-normal)',
      }}
    >
      <ShieldAlert size={15} strokeWidth={1.75} aria-hidden="true" style={{ flex: 'none', marginTop: 1 }} />
      <span style={{ flex: 1 }}>{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss sign-in error"
        style={{
          border: 0,
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          font: 'inherit',
          fontWeight: 'var(--fw-medium)',
          padding: 0,
        }}
      >
        Dismiss
      </button>
    </div>
  )
}

function LockoutAlert({
  lockedUntil,
  remainingMs,
}: {
  lockedUntil: number | null
  remainingMs: number
}) {
  const absolute = lockedUntil ? new Date(lockedUntil).toISOString() : undefined
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--s-2)',
        padding: 'var(--s-3)',
        borderRadius: 'var(--r-sm)',
        background: 'var(--err-100)',
        border: '1px solid var(--err-200)',
        color: 'var(--err-700)',
        fontSize: 'var(--fs-13)',
        lineHeight: 'var(--lh-normal)',
      }}
    >
      <ShieldAlert size={15} strokeWidth={1.75} aria-hidden="true" style={{ flex: 'none', marginTop: 1 }} />
      <div>
        {/* Announced once; the ticking countdown stays out of the live region. */}
        <span role="alert">Too many failed attempts. Sign-in is locked for 30 minutes.</span>
        <span aria-hidden="true" style={{ display: 'block', marginTop: 2 }}>
          Try again in{' '}
          <span className="mono" style={{ color: 'inherit' }} title={absolute}>
            {formatMmSs(remainingMs)}
          </span>
        </span>
      </div>
    </div>
  )
}

function ErrorAlert({ message, attemptsLeft }: { message: string; attemptsLeft: number }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--s-2)',
        padding: 'var(--s-3)',
        borderRadius: 'var(--r-sm)',
        background: 'var(--err-100)',
        border: '1px solid var(--err-200)',
        color: 'var(--err-700)',
        fontSize: 'var(--fs-13)',
        lineHeight: 'var(--lh-normal)',
      }}
    >
      <ShieldAlert size={15} strokeWidth={1.75} aria-hidden="true" style={{ flex: 'none', marginTop: 1 }} />
      <div>
        {message}
        {attemptsLeft > 0 && attemptsLeft < MAX_ATTEMPTS && (
          <span style={{ display: 'block', marginTop: 2, opacity: 0.85 }}>
            {attemptsLeft} attempt{attemptsLeft === 1 ? '' : 's'} remaining before lockout.
          </span>
        )}
      </div>
    </div>
  )
}

function OrDivider() {
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--s-3)',
        margin: 'var(--s-5) 0',
      }}
    >
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <span className="kicker">or</span>
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  )
}

function DemoTip() {
  return (
    <div
      style={{
        marginTop: 'var(--s-8)',
        padding: 'var(--s-3)',
        borderRadius: 'var(--r-sm)',
        background: 'var(--bg-sunk)',
        border: '1px solid var(--border)',
        fontSize: 'var(--fs-12)',
        lineHeight: 'var(--lh-loose)',
        color: 'var(--text-mute)',
      }}
    >
      <span className="kicker" style={{ display: 'block', marginBottom: 2 }}>
        Mock mode
      </span>
      Any email signs in. Use <code className="mono">wrong@example.com</code> to exercise the
      lockout flow.
    </div>
  )
}
